import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CalendarFeed,
  CreateVisitPayload,
  UpdateVisitPayload,
  VisitDetail,
  VisitListItem,
  VisitStatus
} from './types';

interface VisitListParams {
  from?: string;
  to?: string;
  customerId?: string;
  status?: string;
}

export function useVisitList(params: VisitListParams) {
  return useQuery({
    queryKey: ['visits', params],
    queryFn: async () => {
      const res = await apiClient.get<VisitListItem[]>('/api/v1/visits', { params });
      return res.data;
    },
    staleTime: 15_000,
    placeholderData: (prev) => prev
  });
}

export function useVisit(id: string | undefined) {
  return useQuery({
    queryKey: ['visit', id],
    queryFn: async () => {
      const res = await apiClient.get<VisitDetail>(`/api/v1/visits/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 15_000
  });
}

export function useCreateVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateVisitPayload) => {
      const res = await apiClient.post<VisitDetail>('/api/v1/visits', payload);
      return res.data;
    },
    onSuccess: (detail) => {
      qc.invalidateQueries({ queryKey: ['visits'] });
      qc.setQueryData(['visit', detail.id], detail);
    }
  });
}

export function useUpdateVisit(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateVisitPayload) => {
      await apiClient.put(`/api/v1/visits/${id}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visits'] });
      qc.invalidateQueries({ queryKey: ['visit', id] });
    }
  });
}

export function useUpdateVisitStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: VisitStatus }) => {
      await apiClient.patch(`/api/v1/visits/${id}/status`, { status });
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['visits'] });
      qc.invalidateQueries({ queryKey: ['visit', id] });
    }
  });
}

export function useDeleteVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/visits/${id}`);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visits'] })
  });
}

export function useEmailVisitIcs(id: string) {
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ outboxId: string; status: string }>(
        `/api/v1/visits/${id}/email-ics`
      );
      return res.data;
    }
  });
}

// ─── Tenant calendar feed ────────────────────────────────────────────────────

export function useCalendarFeed() {
  return useQuery({
    queryKey: ['tenant-calendar-feed'],
    queryFn: async () => {
      const res = await apiClient.get<CalendarFeed | null>('/api/v1/tenant/calendar-feed');
      return res.data;
    },
    staleTime: 60_000
  });
}

export function useIssueCalendarFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<CalendarFeed>('/api/v1/tenant/calendar-feed');
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-calendar-feed'] })
  });
}

export function useRevokeCalendarFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/api/v1/tenant/calendar-feed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-calendar-feed'] })
  });
}
